/**
 * ManualOrderCleanupPanel — paste any order number / id (one per line or
 * comma-separated), preview impact, then choose Mark as test data,
 * Archive, or Delete. Built for the case where the admin tested with
 * their own real-looking account and the auto-detector won't catch it.
 *
 * Three actions:
 *   - Mark as test data → `is_test_data=true` on order + linked
 *     transactions; hides from default Orders list + analytics
 *     (analytics exclusion is a separate work item but the tag is set).
 *   - Archive → soft, sets status='archived', records who/why/when.
 *   - Delete → reuses the existing /cleanup-orders endpoint with
 *     `confirm_text="DELETE SELECTED ORDERS"`.
 */
import React, { useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Search, Trash2, Archive, FlaskConical, AlertTriangle, X, CheckSquare, Square,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtEUR = (n) => new Intl.NumberFormat('en-EU', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

async function api(token, method, path, body) {
  const r = await fetch(`${API_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j?.detail || `HTTP ${r.status}`);
  }
  return r.json();
}

export default function ManualOrderCleanupPanel({ token, onApplied }) {
  const [raw, setRaw] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);          // { found, requested, not_found, orders }
  const [selected, setSelected] = useState(new Set());
  const [action, setAction] = useState(null);          // 'mark' | 'archive' | 'delete' | null
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [running, setRunning] = useState(false);
  const [lastSummary, setLastSummary] = useState(null);

  const parsedInputs = raw
    .split(/[,\s\n]+/g)
    .map((s) => s.trim())
    .filter(Boolean);

  const lookup = useCallback(async () => {
    if (parsedInputs.length === 0) { toast.error('Paste at least one order number or id'); return; }
    setSearching(true); setResult(null); setSelected(new Set()); setAction(null); setLastSummary(null);
    try {
      const d = await api(token, 'POST', '/api/admin/maintenance/manual-cleanup/lookup', {
        order_inputs: parsedInputs,
        email_filter: emailFilter.trim() || null,
      });
      setResult(d);
      // Auto-select everything we found — admin typed them on purpose.
      setSelected(new Set(d.orders.map((o) => o.order_id)));
      if (d.not_found?.length) {
        toast.warning(`${d.not_found.length} not found: ${d.not_found.slice(0, 5).join(', ')}${d.not_found.length > 5 ? '…' : ''}`);
      }
    } catch (e) { toast.error(e.message || 'Lookup failed'); }
    finally { setSearching(false); }
  }, [token, parsedInputs, emailFilter]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectedRows = result?.orders.filter((o) => selected.has(o.order_id)) || [];
  const linkedSum = selectedRows.reduce((acc, o) => {
    acc.transactions += o.linked.transactions;
    acc.messages     += o.linked.messages;
    acc.files        += o.linked.files;
    acc.evc          += o.linked.evc_purchases;
    acc.amount       += o.amount || 0;
    acc.tx_amount    += o.linked.tx_amount || 0;
    return acc;
  }, { transactions: 0, messages: 0, files: 0, evc: 0, amount: 0, tx_amount: 0 });

  const apply = async () => {
    if (selectedRows.length === 0) { toast.error('Select at least one order'); return; }
    if (action === 'delete' && confirmText !== 'DELETE SELECTED ORDERS') {
      toast.error('Type DELETE SELECTED ORDERS to confirm'); return;
    }
    setRunning(true);
    try {
      if (action === 'mark') {
        const r = await api(token, 'POST', '/api/admin/maintenance/manual-cleanup/mark-test', {
          order_ids: selectedRows.map((o) => o.order_id),
        });
        setLastSummary({ kind: 'mark', text: `Marked ${r.tagged_orders} order(s) + ${r.tagged_transactions} transaction(s) as test data` });
        toast.success(`Marked ${r.tagged_orders} order(s) as test data`);
      } else if (action === 'archive') {
        const r = await api(token, 'POST', '/api/admin/maintenance/manual-cleanup/archive', {
          order_ids: selectedRows.map((o) => o.order_id),
          reason: reason.trim() || null,
        });
        setLastSummary({ kind: 'archive', text: `Archived ${r.archived} order(s)` });
        toast.success(`Archived ${r.archived} order(s)`);
      } else if (action === 'delete') {
        const r = await api(token, 'POST', '/api/admin/maintenance/cleanup-orders', {
          order_ids: selectedRows.map((o) => o.order_id),
          confirm_text: 'DELETE SELECTED ORDERS',
          recompute_balances: true,
        });
        const totalDeleted = Object.values(r.deleted || {}).reduce((a, b) => a + (b || 0), 0);
        setLastSummary({
          kind: 'delete',
          text: `Deleted ${totalDeleted} record(s) across ${selectedRows.length} order(s) · ${r.users_recomputed?.length || 0} wallet(s) recomputed`,
          recomputed: r.users_recomputed,
        });
        toast.success(`Deleted ${selectedRows.length} order(s)`);
      }
      // Re-fetch lookup so the UI reflects the new state (archived /
      // tagged) without admin needing to re-paste.
      lookup();
      onApplied && onApplied();
      setAction(null); setConfirmText(''); setReason('');
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally { setRunning(false); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5" data-testid="manual-cleanup-card">
      <header className="flex items-start gap-3 mb-4">
        <Search className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-800">Manual order cleanup</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Paste any order numbers or order ids (comma / newline separated). Preview links, then choose to Mark as test data, Archive, or Delete.
            Preview is mandatory before any destructive action.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        <div className="lg:col-span-2">
          <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block mb-0.5">Order numbers / ids</label>
          <textarea rows={3} value={raw} onChange={(e) => setRaw(e.target.value)}
                    data-testid="mcp-input"
                    placeholder="25052026-1, 25052026-2&#10;or one per line"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-violet-500 outline-none font-mono" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block mb-0.5">Email filter (optional)</label>
          <input type="text" value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)}
                 data-testid="mcp-email-filter"
                 placeholder="admin@…"
                 className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 focus:border-violet-500 outline-none" />
          <button onClick={lookup} disabled={searching || parsedInputs.length === 0}
                  data-testid="mcp-lookup-btn"
                  className="mt-2 w-full h-9 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-xs font-bold flex items-center justify-center gap-1.5">
            {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            Look up {parsedInputs.length > 0 ? `(${parsedInputs.length})` : ''}
          </button>
        </div>
      </div>

      {result && (
        <>
          {/* Summary row */}
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-bold">
              Found <span className="font-mono">{result.found}</span> / {result.requested}
            </span>
            {result.not_found?.length > 0 && (
              <span className="px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 font-bold">
                Not found: {result.not_found.length}
              </span>
            )}
            <span className="px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 font-bold">
              Selected: <span className="font-mono">{selected.size}</span>
            </span>
          </div>

          {/* Table */}
          {result.orders.length > 0 ? (
            <div className="overflow-x-auto border border-slate-200 rounded-lg mb-3">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 font-bold uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="px-2 py-2 w-8 whitespace-nowrap"></th>
                    <th className="px-2 py-2 text-left whitespace-nowrap">Order #</th>
                    <th className="px-2 py-2 text-left whitespace-nowrap">Date</th>
                    <th className="px-2 py-2 text-left whitespace-nowrap">Customer</th>
                    <th className="px-2 py-2 text-left whitespace-nowrap">Status</th>
                    <th className="px-2 py-2 text-left whitespace-nowrap">Vehicle</th>
                    <th className="px-2 py-2 text-right whitespace-nowrap">Amount</th>
                    <th className="px-2 py-2 text-right whitespace-nowrap">Linked</th>
                    <th className="px-2 py-2 text-left whitespace-nowrap">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {result.orders.map((o, idx) => {
                    const checked = selected.has(o.order_id);
                    return (
                      <tr key={o.order_id} data-testid={`mcp-row-${idx}`}
                          className={`border-t border-slate-100 ${checked ? 'bg-violet-50' : 'hover:bg-slate-50'}`}>
                        <td className="px-2 py-1.5 text-center">
                          <button onClick={() => toggle(o.order_id)} data-testid={`mcp-check-${idx}`}
                                  className={`w-5 h-5 inline-flex items-center justify-center rounded ${checked ? 'text-violet-600' : 'text-slate-400 hover:text-slate-600'}`}>
                            {checked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-slate-700">{o.order_number || o.order_id.slice(0, 8)}</td>
                        <td className="px-2 py-1.5 text-slate-500">{(o.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                        <td className="px-2 py-1.5 text-slate-700 truncate max-w-[160px]" title={o.user_email}>{o.user_email || '—'}</td>
                        <td className="px-2 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            o.status === 'archived' ? 'bg-slate-200 text-slate-700'
                            : o.status === 'cancelled' ? 'bg-rose-100 text-rose-700'
                            : o.status === 'completed' ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-slate-100 text-slate-700'
                          }`}>{o.status}</span>
                          {o.is_test_data && <span className="ml-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold">TEST</span>}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600 truncate max-w-[180px]" title={`${o.manufacturer || ''} ${o.model || ''}`.trim()}>
                          {[o.manufacturer, o.model].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-slate-700">{fmtEUR(o.amount)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-500 text-[10px]">
                          {o.linked.transactions}tx · {o.linked.messages}m · {o.linked.files}f
                          {o.linked.evc_purchases > 0 && ` · ${o.linked.evc_purchases}evc`}
                        </td>
                        <td className="px-2 py-1.5 text-amber-700 text-[10px] max-w-[200px]">
                          {o.warnings?.length > 0 ? (
                            <ul className="space-y-0.5">
                              {o.warnings.map((w, j) => <li key={j} className="truncate" title={w}>• {w}</li>)}
                            </ul>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-500 text-center mb-3">
              No matching orders. Check the order number / id or your email filter.
            </div>
          )}

          {/* Action picker */}
          {selectedRows.length > 0 && !action && (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs text-slate-600 font-bold mr-2">
                Choose action for {selectedRows.length} selected:
              </span>
              <button onClick={() => setAction('mark')} data-testid="mcp-action-mark"
                      className="h-9 px-3 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-bold flex items-center gap-1.5">
                <FlaskConical className="w-3.5 h-3.5" /> Mark as test data
              </button>
              <button onClick={() => setAction('archive')} data-testid="mcp-action-archive"
                      className="h-9 px-3 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold flex items-center gap-1.5">
                <Archive className="w-3.5 h-3.5" /> Archive
              </button>
              <button onClick={() => setAction('delete')} data-testid="mcp-action-delete"
                      className="h-9 px-3 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-bold flex items-center gap-1.5">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          )}

          {/* Confirm card per action */}
          {action && (
            <div className={`rounded-xl border p-4 mb-2 ${
              action === 'delete' ? 'bg-rose-50 border-rose-200'
              : action === 'archive' ? 'bg-slate-50 border-slate-200'
              : 'bg-amber-50 border-amber-200'
            }`} data-testid={`mcp-confirm-${action}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-start gap-2">
                  {action === 'delete'
                    ? <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5" />
                    : action === 'archive'
                    ? <Archive className="w-4 h-4 text-slate-600 mt-0.5" />
                    : <FlaskConical className="w-4 h-4 text-amber-600 mt-0.5" />}
                  <div>
                    <div className="text-sm font-bold text-slate-800">
                      {action === 'delete'  && `Permanently delete ${selectedRows.length} order(s)?`}
                      {action === 'archive' && `Archive ${selectedRows.length} order(s)?`}
                      {action === 'mark'    && `Mark ${selectedRows.length} order(s) as test data?`}
                    </div>
                    <div className="text-[11px] text-slate-600 mt-0.5">
                      Impact: <span className="font-mono">{linkedSum.transactions}</span> tx (€{linkedSum.tx_amount.toFixed(2)}) ·{' '}
                      <span className="font-mono">{linkedSum.messages}</span> msg ·{' '}
                      <span className="font-mono">{linkedSum.files}</span> files ·{' '}
                      <span className="font-mono">{linkedSum.evc}</span> evc · order total €{linkedSum.amount.toFixed(2)}
                      {action === 'mark'    && '. Orders + tx tagged is_test_data, removed from default lists & analytics.'}
                      {action === 'archive' && '. status="archived", historical data preserved, removed from default Orders list.'}
                      {action === 'delete'  && '. Orders + linked tx/msgs/files/evc DELETED. Wallets recomputed. Irreversible.'}
                    </div>
                  </div>
                </div>
                <button onClick={() => { setAction(null); setConfirmText(''); setReason(''); }}
                        data-testid="mcp-confirm-cancel"
                        className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
              </div>

              <div className="flex flex-wrap items-end gap-2 mt-2">
                {action === 'archive' && (
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block mb-0.5">Reason (optional)</label>
                    <input value={reason} onChange={(e) => setReason(e.target.value)}
                           data-testid="mcp-reason"
                           placeholder="e.g. testing my own account"
                           className="w-full h-9 px-2 text-sm rounded-lg border border-slate-200 outline-none focus:border-slate-400" />
                  </div>
                )}
                {action === 'delete' && (
                  <div className="flex-1 min-w-[260px]">
                    <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block mb-0.5">
                      Type <span className="font-mono text-rose-700">DELETE SELECTED ORDERS</span> to confirm
                    </label>
                    <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                           data-testid="mcp-confirm-text"
                           placeholder="DELETE SELECTED ORDERS"
                           className="w-full h-9 px-2 text-sm rounded-lg border border-slate-200 focus:border-rose-500 outline-none font-mono" />
                  </div>
                )}
                <button onClick={apply}
                        disabled={running || (action === 'delete' && confirmText !== 'DELETE SELECTED ORDERS')}
                        data-testid="mcp-apply"
                        className={`h-9 px-4 rounded-lg text-white text-xs font-bold flex items-center gap-1.5 disabled:bg-slate-300 disabled:cursor-not-allowed ${
                          action === 'delete' ? 'bg-rose-600 hover:bg-rose-700'
                          : action === 'archive' ? 'bg-slate-700 hover:bg-slate-800'
                          : 'bg-amber-500 hover:bg-amber-600'
                        }`}>
                  {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  Apply
                </button>
              </div>
            </div>
          )}

          {lastSummary && (
            <div className={`rounded-lg border p-3 text-xs ${
              lastSummary.kind === 'delete' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : lastSummary.kind === 'archive' ? 'bg-slate-50 border-slate-200 text-slate-700'
              : 'bg-amber-50 border-amber-200 text-amber-800'
            }`} data-testid="mcp-last-summary">
              {lastSummary.text}
              {lastSummary.recomputed?.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {lastSummary.recomputed.map((u) => (
                    <li key={u.user_id}>
                      <span className="font-mono">{u.email}</span>: {fmtEUR(u.before)} → {fmtEUR(u.after)}
                      <span className={`ml-1 font-bold ${u.diff >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        ({u.diff >= 0 ? '+' : ''}{fmtEUR(u.diff)})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
